// Client settings, persisted in localStorage.

import { BINDS_VERSION, migrateBinds, normalizeBinds, type Binds, DEFAULT_BINDS, cloneBinds } from './binds';

export type PhysicsMode = 'etjump' | 'vanilla';

/** Which HUD modules are shown and how the HUD is laid out. */
export interface HudConfig {
  timer: boolean;
  speed: boolean;
  keys: boolean;
  splits: boolean;
  cgaz: boolean;
  health: boolean;
  chat: boolean;
  debug: boolean;
  /** HUD scale, 0.75 .. 2. */
  scale: number;
  /** Distance of the HUD from the screen edges in % of the screen. */
  safeMargin: number;
  /** Speedometer colour follows acceleration (green gaining, red losing). */
  speedColor: boolean;
  /** Vertical speed under the speedometer. */
  verticalSpeed: boolean;
  // ETJump training HUDs
  /** Overbounce detector (etj_drawOB); 2 also shows sticky overbounces. */
  obDetector: boolean;
  obSticky: boolean;
  jumpSpeeds: boolean;
  snaphud: boolean;
  upmoveMeter: boolean;
  strafeQuality: boolean;
  maxSpeed: boolean;
}

export interface Settings {
  /** ET "sensitivity"; degrees per mouse count = sensitivity * m_yaw (0.022). */
  sensitivity: number;
  invertMouse: boolean;
  /** Pointer lock with unadjustedMovement (no OS mouse acceleration) where supported. */
  rawInput: boolean;
  /** Mouse DPI, only used to show cm/360. */
  mouseDpi: number;
  /** Horizontal field of view at 4:3, widened for wider screens (Hor+). */
  fov: number;
  /** Physics frame length in ms: 8 = 125 fps (default, like pmove_msec 8), 3 = 333 fps. */
  physicsMsec: number;
  brightness: number;
  /** Multiplier of the device pixel ratio for the 3D view (0.5 .. 2). */
  renderScale: number;
  /** Low graphics preset: 1x pixel ratio at 80 %, no anisotropic filtering, no antialiasing, one cloud layer. */
  lowGraphics: boolean;
  /** The GPU was looked at once (the low preset is suggested for weak ones). */
  gpuChecked: boolean;
  showFps: boolean;
  showHelp: boolean;
  /** Hide the controls help a few seconds after the game is resumed. */
  autoHideHelp: boolean;
  /** Third person camera (cg_thirdPerson), its distance (cg_thirdPersonRange) and angle around the player (cg_thirdPersonAngle). */
  thirdPerson: boolean;
  /** Draw the map's recorded trickjump line. */
  showTjl: boolean;
  /** The knife in the hand in first person (cg_drawGun). */
  drawGun: boolean;
  thirdPersonRange: number;
  thirdPersonAngle: number;
  /** Sound volumes, 0..1: everything, the player's sounds, the map's sounds (speakers, doors). */
  volume: number;
  effectsVolume: number;
  ambientVolume: number;
  /** Footsteps and landings of other players. */
  otherPlayerSounds: boolean;
  hud: HudConfig;
  binds: Binds;
  /** Menu scale, 0.75 .. 2. */
  uiScale: number;
  reduceMotion: boolean;
  /** Larger HUD text (on top of the HUD scale). */
  largeHudText: boolean;
  /** High contrast accent and lines. */
  highContrast: boolean;
  playerName: string;
  /** The team picked last (Axis 0, Allies 1): focused on the team choice when a map starts. */
  team: 0 | 1;
  /** Version of the stored binds (BINDS_VERSION): older ones are migrated once. */
  bindsVersion: number;
  favorites: string[];
  /** Most recent first. */
  recentMaps: { name: string; at: number }[];
}

export const DEFAULT_HUD: HudConfig = {
  timer: true,
  speed: true,
  keys: true,
  splits: true,
  cgaz: true,
  health: true,
  chat: true,
  debug: false,
  scale: 1,
  safeMargin: 5,
  speedColor: true,
  verticalSpeed: false,
  obDetector: false,
  obSticky: false,
  jumpSpeeds: false,
  snaphud: false,
  upmoveMeter: false,
  strafeQuality: false,
  maxSpeed: false,
};

export const DEFAULT_SETTINGS: Settings = {
  sensitivity: 2.5,
  invertMouse: false,
  rawInput: true,
  mouseDpi: 800,
  fov: 90,
  physicsMsec: 8,
  brightness: 1,
  renderScale: 1,
  lowGraphics: false,
  gpuChecked: false,
  showFps: false,
  showHelp: true,
  autoHideHelp: false,
  thirdPerson: false,
  showTjl: true,
  drawGun: true,
  thirdPersonRange: 120,
  thirdPersonAngle: 0,
  volume: 0.7,
  effectsVolume: 1,
  ambientVolume: 0.8,
  otherPlayerSounds: true,
  hud: DEFAULT_HUD,
  binds: DEFAULT_BINDS,
  uiScale: 1,
  reduceMotion: false,
  largeHudText: false,
  highContrast: false,
  playerName: 'player',
  team: 0,
  bindsVersion: BINDS_VERSION,
  favorites: [],
  recentMaps: [],
};

export const M_YAW = 0.022;
export const M_PITCH = 0.022;

const SETTINGS_KEY = 'tjgame.settings';
/** What earlier versions stored: personal records and their replays (removed when the settings load). */
const OLD_RECORDS_KEY = 'tjgame.records';
const OLD_REPLAY_PREFIX = 'tjgame.replay:';
const MAX_RECENT = 12;

/** A fresh copy of the defaults (nested objects included). */
export function defaultSettings(): Settings {
  return { ...DEFAULT_SETTINGS, hud: { ...DEFAULT_HUD }, binds: cloneBinds(DEFAULT_BINDS), favorites: [], recentMaps: [] };
}

/**
 * Settings from a stored object of any older version: unknown keys are kept
 * out, missing ones get defaults, nested objects are merged key by key and
 * fields that moved (showCgaz -> hud.cgaz) are carried over.
 */
export function migrateSettings(stored: unknown): Settings {
  const s = defaultSettings();
  if (!stored || typeof stored !== 'object') return s;
  const raw = stored as Record<string, unknown>;
  const d = DEFAULT_SETTINGS as unknown as Record<string, unknown>;
  const out = s as unknown as Record<string, unknown>;
  for (const key of Object.keys(d)) {
    if (key === 'hud' || key === 'binds' || !(key in raw)) continue;
    const v = raw[key];
    if (Array.isArray(d[key]) ? Array.isArray(v) : typeof v === typeof d[key]) out[key] = v;
  }
  const hud = (raw.hud && typeof raw.hud === 'object' ? raw.hud : {}) as Record<string, unknown>;
  const hudOut = s.hud as unknown as Record<string, unknown>;
  for (const key of Object.keys(DEFAULT_HUD)) if (typeof hud[key] === typeof hudOut[key]) hudOut[key] = hud[key];
  // before the HUD settings the strafe helper was a top level switch
  if (typeof raw.showCgaz === 'boolean' && !('cgaz' in hud)) s.hud.cgaz = raw.showCgaz;
  // binds saved before bindsVersion existed are version 1
  const bindsFrom = typeof raw.bindsVersion === 'number' ? raw.bindsVersion : raw.binds ? 1 : BINDS_VERSION;
  s.binds = migrateBinds(normalizeBinds(raw.binds), bindsFrom);
  s.bindsVersion = BINDS_VERSION;
  s.favorites = s.favorites.filter((x) => typeof x === 'string');
  if (s.team !== 0 && s.team !== 1) s.team = 0;
  s.recentMaps = s.recentMaps.filter((x) => x && typeof x.name === 'string' && typeof x.at === 'number').slice(0, MAX_RECENT);
  return s;
}

export function loadSettings(): Settings {
  try {
    removeOldRecords();
    return migrateSettings(JSON.parse(localStorage.getItem(SETTINGS_KEY) ?? '{}'));
  } catch {
    return defaultSettings();
  }
}

/** Drops the personal records and record replays earlier versions kept. */
function removeOldRecords(): void {
  localStorage.removeItem(OLD_RECORDS_KEY);
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (key?.startsWith(OLD_REPLAY_PREFIX)) localStorage.removeItem(key);
  }
}

export function saveSettings(s: Settings): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
  } catch (err) {
    console.warn('could not save settings', err);
  }
}

/** Moves `item` to the front of a most-recent-first list. */
export function pushRecent<T>(list: T[], item: T, same: (a: T, b: T) => boolean = (a, b) => a === b): T[] {
  return [item, ...list.filter((x) => !same(x, item))].slice(0, MAX_RECENT);
}

/** Shared, live settings: the game reads them every frame, the menus update them. */
export interface SettingsStore {
  get(): Settings;
  update(patch: Partial<Settings>): void;
}

export function formatTime(ms: number): string {
  const sign = ms < 0 ? '-' : '';
  ms = Math.abs(Math.round(ms));
  const m = Math.floor(ms / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  const r = ms % 1000;
  return `${sign}${m > 0 ? `${m}:${String(s).padStart(2, '0')}` : s}.${String(r).padStart(3, '0')}`;
}
